import { TypedDocumentNode, gql } from '@apollo/client';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Position, Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import { glossary } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import {
  LastScores,
  Props as LastScoresProps,
} from 'components/stats/LastScores';

import { BuyingFloorPricePlayerDetails } from './BuyingFloorPricePlayerDetails';
import { BuyingPlayerDetailsFooter } from './BuyingPlayerDetailsFooter';
import { PlayerInjuryReport } from './PlayerInjuryReport';
import { PlayerNewsUpdates } from './PlayerNewsUpdates';
import { PlayerProperties } from './PlayerProperties';
import { PlayerUnavailabilityPanel } from './PlayerUnavailabilityPanel';
import { PlayerUpcomingGames } from './PlayerUpcomingGames';
import {
  FixtureScoresQuery,
  FixtureScoresQueryVariables,
  PlayerDetailsQuery,
  PlayerDetailsQueryVariables,
} from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 4 })`
  position: relative;
  padding: var(--double-unit);
  color: var(--c-white);
  background: var(--c-black);
  @media ${laptopAndAbove} {
    background: transparent;
  }
`;

const Footer = styled(Vertical).attrs({ gap: 2 })`
  position: sticky;
  bottom: var(--double-unit);
  background: transparent;
`;

const FIXTURE_SCORES_QUERY = gql`
  query FixtureScoresQuery(
    $slug: String!
    $cursor: String
    $position: Position
  ) {
    anyPlayer(slug: $slug) {
      slug
      allPlayerGameScores(first: 10, after: $cursor, position: $position) {
        nodes {
          id
          ...LastScores_playerGameScoreInterface
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
      ...LastScores_anyPlayer
    }
  }
  ${LastScores.fragments.playerGameScoreInterface}
  ${LastScores.fragments.anyPlayer}
` as TypedDocumentNode<FixtureScoresQuery, FixtureScoresQueryVariables>;

const PLAYER_DETAILS_QUERY = gql`
  query PlayerDetailsQuery(
    $playerSlug: String!
    $cardSlug: String!
    $so5LeaderboardSlug: String
    $shouldFetchCard: Boolean!
    $inSeason: Boolean
    $rarity: Rarity
    $withBuyingOptions: Boolean!
    $onlyPrimary: Boolean = false
  ) {
    anyPlayer(slug: $playerSlug) {
      slug
      activeClub {
        slug
        upcomingGames(first: 3) {
          id
          ...PlayerUpcomingGames_game
        }
      }
      activeNationalTeam {
        slug
        upcomingGames(first: 3) {
          id
          ...PlayerUpcomingGames_game
        }
      }
      ...PlayerProperties_anyPlayer
      ...PlayerUnavailabilityPanel_anyPlayer
      ...PlayerInjuryReport_anyPlayer
      ...PlayerNewsUpdates_anyPlayer
      ...BuyingPlayerDetailsFooter_anyPlayer
      ...BuyingFloorPricePlayerDetails_anyPlayer
    }
    anyCard(slug: $cardSlug) @include(if: $shouldFetchCard) {
      slug
      anyPositions
      ...LastScores_anyCard
      ...PlayerProperties_anyCard
    }
  }
  ${LastScores.fragments.anyCard}
  ${PlayerUpcomingGames.fragments.game}
  ${PlayerProperties.fragments.anyPlayer}
  ${PlayerProperties.fragments.anyCard}
  ${PlayerUnavailabilityPanel.fragments.anyPlayer}
  ${PlayerInjuryReport.fragments.anyPlayer}
  ${PlayerNewsUpdates.fragments.anyPlayer}
  ${BuyingPlayerDetailsFooter.fragments.anyPlayer}
  ${BuyingFloorPricePlayerDetails.fragments.anyPlayer}
` as TypedDocumentNode<PlayerDetailsQuery, PlayerDetailsQueryVariables>;

type Props = {
  playerSlug: string;
  cardSlug?: string;
  onClose?: () => void;
  extraActions?: ReactNode;
  so5LeaderboardSlug?: string;
  onScoreSelect?: LastScoresProps['onScoreSelect'];
  positions?: Position[];
  rivals?: boolean;
  withBuyingOptions?: {
    rarity: Rarity;
    inSeason: boolean;
    onMarketClick: (playerSlug: string) => void;
  };
};

export const PlayerDetails = ({
  playerSlug,
  cardSlug,
  so5LeaderboardSlug,
  onClose,
  extraActions,
  onScoreSelect,
  positions,
  rivals,
  withBuyingOptions,
}: Props) => {
  const { formatMessage } = useIntl();

  const { data: playerDetailsData, loading: playerDetailsLoading } = useQuery(
    PLAYER_DETAILS_QUERY,
    {
      variables: {
        playerSlug,
        so5LeaderboardSlug,
        cardSlug: cardSlug || '',
        shouldFetchCard: !!cardSlug,
        inSeason: withBuyingOptions?.inSeason,
        rarity: withBuyingOptions?.rarity,
        withBuyingOptions: !!withBuyingOptions,
      },
    }
  );

  const { anyCard } = playerDetailsData || {};
  const cardPositions = [...(anyCard?.anyPositions || [])].reverse();
  const position =
    positions?.find(p => cardPositions.includes(p)) || cardPositions[0];
  const {
    data: fixtureScoresData,
    loading: fixtureScoresLoading,
    loadMore,
  } = usePaginatedQuery(FIXTURE_SCORES_QUERY, {
    variables: {
      slug: playerSlug,
      position,
    },
    connection: 'PlayerGameScoreInterfaceConnection',
    skip: Boolean(cardSlug && !position),
  });

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, {
        slug: playerSlug!,
        cursor:
          fixtureScoresData?.anyPlayer.allPlayerGameScores.pageInfo.endCursor,
        position,
      });
    },
    fixtureScoresData?.anyPlayer.allPlayerGameScores.pageInfo.hasNextPage ||
      false,
    fixtureScoresLoading
  );

  if (playerDetailsLoading || !(playerDetailsData && fixtureScoresData)) {
    return (
      <Root>
        <LoadingIndicator fullHeight />
      </Root>
    );
  }

  const upcomingGames = [
    ...(playerDetailsData?.anyPlayer.activeClub?.upcomingGames || []),
    ...(playerDetailsData?.anyPlayer.activeNationalTeam?.upcomingGames || []),
  ];
  return (
    <Root>
      {onClose && (
        <SBHorizontal>
          <IconButton
            onClick={onClose}
            icon={faChevronLeft}
            color="transparent"
            aria-label={formatMessage(glossary.close)}
          />
          {extraActions && <Horizontal>{extraActions}</Horizontal>}
        </SBHorizontal>
      )}
      <PlayerProperties card={anyCard} player={playerDetailsData.anyPlayer} />
      <PlayerInjuryReport player={playerDetailsData.anyPlayer} />
      <PlayerUnavailabilityPanel player={playerDetailsData.anyPlayer} />
      <LastScores
        card={anyCard}
        player={fixtureScoresData?.anyPlayer}
        so5Scores={fixtureScoresData?.anyPlayer.allPlayerGameScores.nodes}
        InfiniteScrollLoader={<InfiniteScrollLoader />}
        onScoreSelect={onScoreSelect}
        inDrawer={rivals}
        floorPrice={
          withBuyingOptions && (
            <BuyingFloorPricePlayerDetails
              anyPlayer={playerDetailsData.anyPlayer}
              inSeason={withBuyingOptions.inSeason}
            />
          )
        }
      />
      <PlayerNewsUpdates player={playerDetailsData.anyPlayer} />
      <PlayerUpcomingGames games={upcomingGames} withRivalsLinks={rivals} />
      {withBuyingOptions && (
        <Footer>
          <BuyingPlayerDetailsFooter
            anyPlayer={playerDetailsData.anyPlayer}
            rarity={withBuyingOptions.rarity}
            inSeason={withBuyingOptions.inSeason}
            onMarketClick={withBuyingOptions.onMarketClick}
          />
        </Footer>
      )}
    </Root>
  );
};
